<script>
import { mapGetters, mapState } from 'vuex'
import { required, minLength } from 'vuelidate/lib/validators'

export default {
  data () {
    return {
      strype: null,
      stripErrorMessage: '',
      loading: false,
      touched: false,
      cardInfo: {
        name: '',
        address_line1: '',
        address_line2: '',
        address_city: '',
        address_state: '',
        address_country: '',
        address_zip: ''
      },
      cardNumberFocused: false,
      cardExpiryFocused: false,
      cardCvcFocused: false,
      cardNumberElement: null,
      cardExpiryElement: null,
      cardCVCElement: null
    }
  },
  validations: {
    cardInfo: {
      name: {
        required,
        minLength: minLength(2)
      },
      address_line1: {
        required
      },
      address_city: {
        required
      },
      address_state: {
        required
      },
      address_country: {
        required
      },
      address_zip: {
        required
      }
    }
  },
  async mounted () {
    this.stripe = Stripe(process.env.VUE_APP_STRIPE_KEY) // eslint-disable-line
    await this.createAndMountFormElements()
  },
  computed: {
    ...mapState(['user', 'home', 'settings', 'challenges', 'challengePage']),
    ...mapGetters(['primaryColor', 'primaryColorRgb', 'loggedIn']),
    card () {
      return (
        this.user &&
        this.user.customer &&
        this.user.customer.sources &&
        this.user.customer.sources.data &&
        this.user.customer.sources.data.length > 0 &&
        this.user.customer.sources.data[0]
          ? this.user.customer.sources.data[0] : this.cardInfo
      )
    }
  },
  methods: {
    createAndMountFormElements () {
      var elements = this.stripe.elements()
      this.cardNumberElement = elements.create('cardNumber', {
        style: {
          base: {
            fontSize: '13px',
            letterSpacing: '0.761905px',
            '::placeholder': {
              color: '#495057',
              backgroundColor: '#f3f3f3'
            },
            ':focus': {
              backgroundColor: 'white',
              '::placeholder': {
                backgroundColor: 'white'
              }
            }
          }
        },
        placeholder: '0000 0000 0000 0000'
      })
      this.cardNumberElement.on('focus', this.focusNumber)
      this.cardNumberElement.on('blur', this.focusNumber)
      this.cardNumberElement.mount('#card-number-element')

      this.cardExpiryElement = elements.create('cardExpiry', {
        style: {
          base: {
            fontSize: '13px',
            letterSpacing: '0.761905px',
            ':focus': {
              backgroundColor: 'white',
              '::placeholder': {
                backgroundColor: 'white'
              }
            },
            '::placeholder': {
              color: '#495057',
              backgroundColor: '#f3f3f3'
            }
          }
        },
        placeholder: 'MM/YY'
      })
      this.cardExpiryElement.on('focus', this.focusExp)
      this.cardExpiryElement.on('blur', this.focusExp)
      this.cardExpiryElement.mount('#card-expiry-element')

      this.cardCvcElement = elements.create('cardCvc', {
        style: {
          base: {
            fontSize: '13px',
            letterSpacing: '0.761905px',
            ':focus': {
              backgroundColor: 'white',
              '::placeholder': {
                backgroundColor: 'white'
              }
            },
            '::placeholder': {
              color: '#495057',
              backgroundColor: '#f3f3f3'
            }
          }
        },
        placeholder: '000'
      })
      this.cardCvcElement.on('focus', this.focusCvc)
      this.cardCvcElement.on('blur', this.focusCvc)
      this.cardCvcElement.mount('#card-cvc-element')
    },
    focusNumber () {
      this.cardNumberFocused = !this.cardNumberFocused
    },
    focusExp () {
      this.cardExpiryFocused = !this.cardExpiryFocused
    },
    focusCvc () {
      this.cardCvcFocused = !this.cardCvcFocused
    },
    async saveChanges (e) {
      e.preventDefault()
      this.cardInfo.name = this.cardInfo.name === '' ? this.card.name : this.cardInfo.name
      this.cardInfo.address_line1 = this.cardInfo.address_line1 === '' ? this.card.address_line1 : this.cardInfo.address_line1

      if (this.cardInfo.address_line2 === '') {
        delete this.cardInfo.address_line2
      }

      this.cardInfo.address_city = this.cardInfo.address_city === '' ? this.card.address_city : this.cardInfo.address_city
      this.cardInfo.address_state = this.cardInfo.address_state === '' ? this.card.address_state : this.cardInfo.address_state
      this.cardInfo.address_country = this.cardInfo.address_country === '' ? this.card.address_country : this.cardInfo.address_country
      this.cardInfo.address_zip = this.cardInfo.address_zip === '' ? this.card.address_zip : this.cardInfo.address_zip

      this.$v.$touch()
      this.touched = true
      if (!this.$v.$invalid) {
        this.loading = true
        this.stripErrorMessage = ''
        await this.stripe.createToken(this.cardNumberElement, this.cardInfo).then(async (result) => {
          if (result.error) {
            this.loading = false
            // Set local state variable to display error in template
            this.stripErrorMessage = result.error
          } else {
            // Got a token. Lets submit it to the backend
            // Just submit the card token to the backend to update user billing
            const stripeToken = result.token
            const response = await this.$store.dispatch('addCard', stripeToken.id)
            if (response && response.data && response.data.success) {
              // Card added succesfully
              await this.$store.dispatch('getUser', this.$store.state.user.token)
              this.loading = false
              this.$emit('setEditCard', false)
            } else {
              this.loading = false
              // Set local state variable to display error in template
              this.stripErrorMessage = response && response.error ? response.error.message : 'Something went wrong'
            }
          }
        })
      }
    }
  },
  watch: {
    cardInfo: {
      handler () {
        if (this.touched) {
          this.$v.$reset()
        }
      }
    }
  }
}
</script>
<template>
  <b-form>
    <b-row>
      <b-col class="card">
        <div class="row">
          <div class="wrapper">
            <div class="top-elements">
              <b-form-group
                id="input-group-1"
                label-for="input-1"
                description=""
              >
                <label>{{this.$_polyglot.t('profile.payment.name.label')}}</label>
                <b-form-input
                  id="input-1"
                  :value="card.name"
                  @input="($event) => cardInfo.name = $event"
                  type="text"
                  :placeholder="this.$_polyglot.t('profile.payment.name.placeholder')"
                  :state="!$v.cardInfo.name.$error"
                ></b-form-input>
                  <b-form-invalid-feedback id="input-live-feedback">
                    {{ this.$_polyglot.t('profile.payment.name.invalid') }}
                  </b-form-invalid-feedback>
              </b-form-group>
              <b-form-group
                id="input-group-8"
                label-for="card-number-element"
                description=""
              >
                <label>{{this.$_polyglot.t('profile.payment.cardlabel')}}</label>
                <div id="card-number-element" :class="{ isFocused: cardNumberFocused }"></div>
                <span
                  v-if="stripErrorMessage &&
                    stripErrorMessage.code &&
                    stripErrorMessage.code === 'incomplete_number'"
                    class="card-error">{{stripErrorMessage.message}}
                </span>
              </b-form-group>
              <b-form-group
                id="input-group-8"
                label-for="card-expiry-element"
                description=""
              >
                <label>{{this.$_polyglot.t('profile.payment.expirylabel')}}</label>
                <div id="card-expiry-element" :class="{ isFocused: cardExpiryFocused }"></div>
                <span
                  v-if="stripErrorMessage &&
                    stripErrorMessage.code &&
                    stripErrorMessage.code === 'incomplete_expiry'"
                    class="card-error">{{stripErrorMessage.message}}
                </span>
                </b-form-group>
              <b-form-group
                id="input-group-8"
                label-for="card-cvc-element"
                description=""
              >
                <label>{{this.$_polyglot.t('profile.payment.cvclabel')}}</label>
                <div id="card-cvc-element" :class="{ isFocused: cardCvcFocused }"></div>
                <span
                  v-if="stripErrorMessage &&
                    stripErrorMessage.code &&
                    stripErrorMessage.code === 'incomplete_cvc'"
                    class="card-error">{{stripErrorMessage.message}}
                </span>
                </b-form-group>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="card">
        <div class="row">
          <div class="wrapper">
            <div class="bottom-elements">
              <b-form-group
                id="input-group-2"
                label-for="input-2"
                description=""
              >
                <label>{{this.$_polyglot.t('profile.payment.address1.label')}}</label>
                <b-form-input
                  id="input-2"
                  :value="card.address_line1"
                  @input="($event) => cardInfo.address_line1 = $event"
                  type="text"
                  :placeholder="this.$_polyglot.t('profile.payment.address1.label')"
                  :state="!$v.cardInfo.address_line1.$error"
                ></b-form-input>
                  <b-form-invalid-feedback id="input-live-feedback">
                    {{ this.$_polyglot.t('profile.payment.address1.invalid') }}
                  </b-form-invalid-feedback>
              </b-form-group>
              <b-form-group
                id="input-group-3"
                label-for="input-3"
                description=""
              >
                <label>{{this.$_polyglot.t('profile.payment.address2.label')}}</label>
                <b-form-input
                  id="input-3"
                  :value="card.address_line2"
                   @input="($event) => cardInfo.address_line2 = $event"
                  type="text"
                  :placeholder="this.$_polyglot.t('profile.payment.address2.label')"
                ></b-form-input>
              </b-form-group>
              <div class="address-wrapper">
                <div class="two-inputs-wrapper">
                  <b-form-group
                    id="input-group-4"
                    label-for="input-4"
                    description=""
                  >
                    <label>{{this.$_polyglot.t('profile.payment.city.label')}}</label>
                    <b-form-input
                      id="input-4"
                      :value="card.address_city"
                       @input="($event) => cardInfo.address_city = $event"
                      type="text"
                      :placeholder="this.$_polyglot.t('profile.payment.city.label')"
                      :state="!$v.cardInfo.address_city.$error"
                    ></b-form-input>
                     <b-form-invalid-feedback id="input-live-feedback">
                       {{ this.$_polyglot.t('profile.payment.city.invalid') }}
                     </b-form-invalid-feedback>
                  </b-form-group>
                  <b-form-group
                    id="input-group-5"
                    label-for="input-5"
                    description=""
                  >
                    <label>{{this.$_polyglot.t('profile.payment.state.label')}}</label>
                    <b-form-input
                      id="input-5"
                      :value="card.address_state"
                      @input="($event) => cardInfo.address_state = $event"
                      type="text"
                      :placeholder="this.$_polyglot.t('profile.payment.state.label')"
                      :state="!$v.cardInfo.address_state.$error"
                    ></b-form-input>
                     <b-form-invalid-feedback id="input-live-feedback">
                       {{ this.$_polyglot.t('profile.payment.state.invalid') }}
                     </b-form-invalid-feedback>
                  </b-form-group>
                </div>
                <div class="two-inputs-wrapper">
                  <b-form-group
                    id="input-group-6"
                    label-for="input-6"
                    description=""
                  >
                    <label>{{this.$_polyglot.t('profile.payment.country.label')}}</label>
                    <b-form-input
                      id="input-6"
                      :value="card.address_country"
                      @input="($event) => cardInfo.address_country = $event"
                      type="text"
                      :placeholder="this.$_polyglot.t('profile.payment.country.label')"
                      :state="!$v.cardInfo.address_country.$error"
                    ></b-form-input>
                    <b-form-invalid-feedback id="input-live-feedback">
                       {{ this.$_polyglot.t('profile.payment.country.invalid') }}
                     </b-form-invalid-feedback>
                  </b-form-group>
                  <b-form-group
                    id="input-group-7"
                    label-for="input-7"
                    description=""
                  >
                    <label>{{this.$_polyglot.t('profile.payment.postal.label')}}</label>
                    <b-form-input
                      id="input-7"
                      :value="card.address_zip"
                      @input="($event) => cardInfo.address_zip = $event"
                      type="text"
                      :placeholder="this.$_polyglot.t('profile.payment.postal.label')"
                      :state="!$v.cardInfo.address_zip.$error"
                    ></b-form-input>
                     <b-form-invalid-feedback id="input-live-feedback">
                        {{ this.$_polyglot.t('profile.payment.postal.invalid') }}
                     </b-form-invalid-feedback>
                  </b-form-group>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
    <span v-if="stripErrorMessage && !stripErrorMessage" class="card-error">{{stripErrorMessage}}</span>
    <span v-if="(stripErrorMessage && stripErrorMessage.code && (stripErrorMessage.code !== 'incomplete_number' && stripErrorMessage.code !== 'incomplete_expiry' && stripErrorMessage.code !== 'incomplete_cvc'))
    " class="card-error">{{stripErrorMessage.message}}</span>
    <BaseButton
      type="submit"
      class="button btn-primary edit"
      @click="saveChanges($event)"
      :disabled="loading"
    >
    <b-spinner v-show="loading" small type="grow"></b-spinner>
    <span v-show="!loading">{{this.$_polyglot.t('profile.payment.formsubmit')}}</span>
    </BaseButton>
  </b-form>
</template>

<style lang="scss" scoped>
@import '@design';

::v-deep .__PrivateStripeElement {
  padding: 5px;
}
::v-deep
#card-cvc-element,
#card-number-element,
#card-expiry-element
 {
  padding-top: 0.6rem;
  height: 2.2rem;
  margin-left: 2%;
  padding-left: 1rem;
  width: 98%;
  border: unset;
  transition: 0.3s all ease-in-out;
  border: unset;
  background-color: #f3f3f3;
  border-radius: 8px;
   &.isFocused {
     background-color: white;
     box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.075) inset,
       0px 0px 5px var(--BGColor);
   }
}

form {
  width: 100%;
  .card {
    position: relative;
    margin-bottom: 0.5rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    border-radius: 8px;
    width: 100%;
  }
  .row {
    margin-left: 0;
    width: 100%;
    .wrapper {
      width: 100%;
      .top-elements {
        width: 100%;
        .is-invalid {
          margin-bottom: 0rem;
          margin-left: 2%;
        }
      }
      .bottom-elements {
        width: 100%;
        padding: 10px;
        #input-group-4 {
          margin-right: 10px;
          width: 50%;
        }
        #input-group-5 {
          width: 50%;
        }
        #input-group-6 {
          margin-right: 10px;
          width: 50%;
        }
        #input-group-7 {
          width: 50%;
        }
        .form-group {
          margin-bottom: 5px;
          input {
            height: 30px;
            &.is-valid {
              background-image: none;
            }
            &.is-invalid {
              margin-bottom: 0.5rem;
            }
          }
        }
        .address-wrapper {
          #input-4 {
            width: 100%;
          }
        }
        .two-inputs-wrapper {
          display: flex;
        }
      }
    }
    label {
      font-family: $base-font-family;
      font-style: normal;
      font-weight: 800;
      font-size: 12px;
      line-height: 22px;
      letter-spacing: 0.1px;
      text-transform: capitalize;
      margin-bottom: unset;
    }
    .form-control {
      height: unset !important;
    }
    input {
      margin-left: 2%;
      width: 98%;
      border: unset;
      background-color: #f3f3f3;
      border-radius: 8px;
      transition: 0.3s all ease-in-out;
      margin-bottom: 1rem;
      padding: 18px;
      padding: 9px !important;
      height: unset;
      font-size: 13px;
      &:focus {
        background-color: white;
        box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.075) inset,
          0px 0px 5px var(--BGColor);
      }
      &:disabled {
        background-color: white;
      }
    }
    textarea:hover,
    input:hover,
    textarea:active,
    input:active,
    textarea:focus,
    input:focus,
    button:focus,
    button:active,
    button:hover,
    label:focus,
    .btn:active,
    .btn.active {
      outline: 0px !important;
      -webkit-appearance: none;
    }
  }
  .card-error {
    margin-top: 0.25rem;
    font-size: 80%;
    color: #dc3545;
  }
  .btn-primary {
      color: white;
      height: 2.5rem;
      padding: 0rem 1.4rem;
      border-radius: 2rem;
      font-size: 1rem;
      font-weight: 600;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 1rem;
      &.edit {
        min-width: 12.2rem;
        font-weight: 400;
        border: 2px solid black;
        background-color: black;
        font-size: 0.9rem;
      }
    }
}
.is-valid {
  background-image: none;
}
</style>
